import React from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import type { PosizioneCampo } from '../../integrations/supabase/types';

const STATO_COLOR: Record<string, string> = {
  operativo: '#22c55e',
  sosta:     '#facc15',
  emergenza: '#ef4444',
  rientro:   '#94a3b8',
};

const TIPO_EMOJI: Record<string, string> = {
  squadra: '👥',
  mezzo:   '🚒',
  vedetta: '👁️',
  dos:     '⭐',
};

function makeCampoIcon(p: PosizioneCampo) {
  const color = STATO_COLOR[p.stato] ?? '#6366f1';
  const emoji = TIPO_EMOJI[p.tipo] ?? '📍';
  const isEmergenza = p.stato === 'emergenza';
  const pulse = isEmergenza
    ? `@keyframes pulse-campo { 0%,100%{box-shadow:0 0 0 0 ${color}88} 50%{box-shadow:0 0 0 10px transparent} }
       animation: pulse-campo 1s infinite;`
    : '';
  const html = `
    <div style="
      display:flex; flex-direction:column; align-items:center; gap:2px;
      font-family:system-ui,sans-serif;
    ">
      <div style="
        width:36px; height:36px; border-radius:50%;
        background:${color}; border:3px solid #fff;
        box-shadow:0 2px 8px rgba(0,0,0,0.5);
        display:flex; align-items:center; justify-content:center;
        font-size:16px; ${pulse}
      ">${emoji}</div>
      <div style="
        background:rgba(0,0,0,0.75); color:#fff;
        padding:1px 5px; border-radius:4px;
        font-size:10px; font-weight:700;
        white-space:nowrap; max-width:90px;
        overflow:hidden; text-overflow:ellipsis;
      ">${p.nome}</div>
    </div>`;
  return L.divIcon({ html, className: '', iconSize: [90, 52], iconAnchor: [45, 18] });
}

function formatAgo(updated_at: string): string {
  const diff = Math.round((Date.now() - new Date(updated_at).getTime()) / 1000);
  if (diff < 60) return `${diff}s fa`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min fa`;
  return `${Math.floor(diff / 3600)}h fa`;
}

interface Props {
  posizioni: PosizioneCampo[];
  visible: boolean;
}

const PosizioniCampoLayer: React.FC<Props> = ({ posizioni, visible }) => {
  if (!visible) return null;

  return (
    <>
      {posizioni.map((p) => {
        if (p.lat == null || p.lon == null) return null;
        return (
          <Marker key={p.device_id} position={[p.lat, p.lon]} icon={makeCampoIcon(p)}>
            <Popup>
              <div style={{ minWidth: 160 }}>
                <strong style={{ fontSize: '0.95rem' }}>{p.nome}</strong>
                <div style={{ marginTop: 4, fontSize: '0.82rem', color: '#475569' }}>
                  Tipo: {TIPO_EMOJI[p.tipo]} {p.tipo}
                </div>
                <div style={{
                  marginTop: 4, fontWeight: 700,
                  color: STATO_COLOR[p.stato] ?? '#6366f1',
                }}>
                  {p.stato.toUpperCase()}
                </div>
                {p.nota && (
                  <div style={{ marginTop: 4, fontSize: '0.82rem', fontStyle: 'italic' }}>
                    {p.nota}
                  </div>
                )}
                <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#94a3b8' }}>
                  {p.lat.toFixed(5)}, {p.lon.toFixed(5)}
                  {p.accuratezza ? ` ±${Math.round(p.accuratezza)}m` : ''}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  Agg. {formatAgo(p.updated_at)}
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

export default PosizioniCampoLayer;
